import React from 'react'

import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { DatePickerFullResponsive } from 'components/datepicker'
import FORMATS from 'constants/formats'
import MODAL_NAMES from 'constants/modalNames'
import { getDayJsObject, belongsToRange, getTodayIfIsPastDate, isBeforeToday } from 'helpers/dates'
import { isEmpty } from 'helpers/utils'
import { getCampaignDates } from 'selectors/campaigns'
import { getEarliestDate, getLatestDate } from 'selectors/searchBoxRules'

import { getDateLimitForMulti } from '../helpers'
import { DatePickerContainer } from '../styled'

const DatePickerWrapper = ({
  isConference,
  isResearch,
  isMulti,
  isRoundTrip,
  legIndex,
  legs,
  dateFromError,
  dateToError,
  isResearchBoxExpanded,
  date,
  handleDateChange,
  sbRuleEarliestDate,
  sbRuleLatestDate,
  campaignDates,
  placeholder
}) => {
  const getDisabledDaysForSearchBoxRules = () => {
    if (!sbRuleEarliestDate || !sbRuleLatestDate) {
      return null
    }

    /* For multidestiny trips , checks ranges of dates between legs
   from leg 2 to 8 (index 1 to 7) before range is given by date
   picked in previous leg, if defined */
    if (isMulti && legIndex > 0) {
      return { before: getDateLimitForMulti(legIndex, legs) }
    }

    /* Range dates restriction resulting from selecting one date as the beginning of the trip,
    are resolved in date range picker component */
    return {
      before: getDayJsObject(sbRuleEarliestDate).toDate(),
      after: getDayJsObject(sbRuleLatestDate).toDate()
    }
  }

  /** @param day is rebuilt to strip away time because react-day-picker passes day with
   * midday hour, so when it is compared to the latest day of the range, it results past
   * that range so it gets disabled
   * Reference from react-day-picker: https://github.com/gpbl/react-day-picker/issues/473
   * Reported in JIRA: https://aerolineas-argentinas.atlassian.net/browse/AAW-9314
   *
   * Ex. Latest date from one range coming from BO was 2020-08-15T03:00:00Z, for local arg date time this
   * converted to 2020-08-15, 00:00, gmt -3
   * Meanwhile, day param provided from date picker was
   * "Sat Aug 15 2020 12:00:00 GMT-0300 (Argentina Standard Time)"
   *
   * This method is passed down to react-day-picker to evaluate if each rendered day should be disabled */
  const isDisabledCampaignDay = day => {
    const dayWithoutTime = new Date(day.getFullYear(), day.getMonth(), day.getDate())

    if (isBeforeToday(dayWithoutTime)) {
      return true
    }

    const matchesToEachRange = campaignDates.map(range => {
      return belongsToRange(dayWithoutTime, range.earliest, range.latest)
    })

    const dayIsNotInCampaignRanges = !matchesToEachRange.some(value => value)

    // multidestiny logic for campaign restrictions, each leg should have enabled
    // same or past dates than the previous leg date
    if (isMulti) {
      const minDateForMulti = getDateLimitForMulti(legIndex, legs)
      const dayIsBeforePreviousLeg = day < minDateForMulti
      return dayIsBeforePreviousLeg || dayIsNotInCampaignRanges
    }

    return dayIsNotInCampaignRanges
  }

  /** If there are campaign dates restriction, a function to determine the campaign ranges is returned
   ** If there are not campaign dates, default searchbox rules are taken
   ** In case of some of these configuration missing, DatePickerFullResponsive will resolve default values
   * Allowed modifiers for react-day-picker: https://react-day-picker.js.org/docs/matching-days
   * */
  const getDisabledDays = () => {
    if (!isEmpty(campaignDates)) {
      return isDisabledCampaignDay
    }

    return getDisabledDaysForSearchBoxRules()
  }

  const getMinDateForSearchbox = () => {
    if (!isEmpty(campaignDates)) {
      const minCampaignDate = getDayJsObject(campaignDates[0].earliest)
      return getTodayIfIsPastDate(minCampaignDate).toDate()
    }

    if (!sbRuleEarliestDate) {
      return null
    }
    return getDayJsObject(sbRuleEarliestDate).toDate()
  }

  const getMaxDateForSearchbox = () => {
    if (!isEmpty(campaignDates)) {
      return getDayJsObject(campaignDates[campaignDates.length - 1].latest).toDate()
    }

    if (!sbRuleLatestDate) {
      return null
    }
    return getDayJsObject(sbRuleLatestDate).toDate()
  }

  return (
    <DatePickerContainer isConference={isConference} isRoundTrip={isRoundTrip}>
      <DatePickerFullResponsive
        index={legIndex}
        width={isConference ? '50%' : undefined}
        disableIndex={!isResearchBoxExpanded && isResearch}
        isExpanded={isResearchBoxExpanded}
        error={dateFromError}
        showRange={isRoundTrip}
        flatStyle={isResearch || isConference}
        placeholder={placeholder}
        onChange={handleDateChange}
        dateToError={dateToError}
        modalName={MODAL_NAMES.SEARCH_BOX_DATE_PICKER_MOBILE(legIndex)}
        /* Every possible restriction from searchbox use cases is passed down here,
         * any further restrictions due to range or default behavior is addressed in
         * DatePickerFullResponsive and its children components*/
        disabledDays={getDisabledDays()}
        /* The month to display in the calendar. This differs from the initialMonth prop,
         ** as it causes the calendar to re-render when its value changes, it is used for
         * multidestination conditional rendering when selecting a previous leg*/
        initialMonth={isMulti ? getDateLimitForMulti(legIndex, legs) : undefined}
        format={FORMATS.DAY_PICKER_FORMAT}
        value={isRoundTrip ? date : date && date.from}
        semiBold={isResearch}
        /* Min and max month to be rendered according to sb rules, if null,
         ** defaults are set in DatePickerFullResponsive */
        minDate={getMinDateForSearchbox()}
        maxDate={getMaxDateForSearchbox()}
        /* Passing both month and minDate is not enough to resolve cases for oneway trip with
        restriction of ranges (for instance, first range available next month) AND multidestiny trips
        that restrict dates based on previous leg, so this props allows DatePickerFullResponsive
        to choose which prop will use depending on the trip type*/
        useMonthOverMinDate={isMulti}
      />
    </DatePickerContainer>
  )
}

/*For landings, there is no initial data until campaign data is fetched, so sbRuleEarliestDate
and sbRuleLatestDate may be undefined
*  For conferences (events), there is an initial searchbox rule fetched and then campaign data is fetched
*  For main searchbox, only searchbox rules should be fetched*/
const mapStateToProps = state => ({
  sbRuleEarliestDate: getEarliestDate(state),
  sbRuleLatestDate: getLatestDate(state),
  campaignDates: getCampaignDates(state)
})

DatePickerWrapper.propTypes = {
  isConference: PropTypes.bool,
  isResearch: PropTypes.bool,
  isMulti: PropTypes.bool,
  isRoundTrip: PropTypes.bool,
  legIndex: PropTypes.number,
  legs: PropTypes.array,
  dateFromError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  dateToError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isResearchBoxExpanded: PropTypes.bool,
  // for round-trip date is an object {from, to}
  date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.object]),
  handleDateChange: PropTypes.func,
  sbRuleEarliestDate: PropTypes.string,
  sbRuleLatestDate: PropTypes.string,
  campaignDates: PropTypes.array,
  placeholder: PropTypes.string
}
export default connect(mapStateToProps)(DatePickerWrapper)
